import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import ArticleCard from "@/components/feature/ArticleCard";
import NewsletterForm from "@/components/feature/NewsletterForm";
import { articles, categories, faqs, hotList, site, voices } from "@/mocks/portal";
import { usePageMeta, injectJsonLd, clearPageJsonLd } from "@/hooks/usePageMeta";

const catName = (slug: string) => categories.find((c) => c.slug === slug)?.name ?? "推荐";

export default function Home() {
  const [activeTab, setActiveTab] = useState("all");
  const [faqOpen, setFaqOpen] = useState(0);
  const [showTop, setShowTop] = useState(false);

  usePageMeta({
    title: "看料站 - 热门内容聚合门户，影视综艺生活话题一站看全",
    description:
      "看料站是面向广大读者的娱乐与热点内容聚合门户，聚焦影视综艺、音乐现场、生活方式与社会话题，以人工精选为你梳理值得看的内容与深度解读，帮你快速定位本周最热、最新的优质内容。",
    keywords: "看料站,内容聚合,娱乐资讯,影视综艺,热门话题,深度解读,内容导航",
    path: "/",
  });

  useEffect(() => {
    const json = [
      { "@type": "WebSite", "@id": "https://0ac9g.cn/#website", name: site.name, url: "https://0ac9g.cn/", inLanguage: "zh-CN" },
      { "@type": "WebPage", name: "看料站首页", url: "https://0ac9g.cn/" },
      { "@type": "Organization", name: site.name, email: site.contact.email },
      { "@type": "ItemList", name: "热门内容", itemListElement: hotList.map((t, i) => ({ "@type": "ListItem", position: i + 1, name: t })) },
      { "@type": "FAQPage", mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) },
    ];
    const el = injectJsonLd(json);
    return () => clearPageJsonLd();
  }, []);

  useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > 600);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const hero = articles[0];
  const sidePicks = articles.filter((a) => a.id !== hero.id).slice(0, 2);
  const hotPicks = articles.filter((a) => a.hot).slice(0, 5);
  const deepRead = articles.filter((a) => a.featured);
  const tabList = activeTab === "all" ? articles : articles.filter((a) => a.category === activeTab);
  const marquee = [...hotList, ...hotList];

  return (
    <div className="flex min-h-screen flex-col bg-background-50 text-foreground-900">
      <SiteHeader />

      <main className="flex-1">
        {/* ===== 首屏 ===== */}
        <section className="mx-auto w-full max-w-[1180px] px-4 pt-2 md:px-6" aria-label="精选头条">
          <div className="grid gap-5 lg:grid-cols-3">
            {/* 主头条 */}
            <article className="group relative overflow-hidden rounded-lg lg:col-span-2">
              <Link to={`/article/${hero.id}`} className="block h-full w-full">
                <div className="relative aspect-[16/10] w-full overflow-hidden md:aspect-[16/8]">
                  <img
                    src={hero.image}
                    alt={hero.title}
                    className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/20 to-transparent" />
                </div>
                <div className="absolute inset-x-0 bottom-0 p-5 md:p-7">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded-full bg-primary-500 px-3 py-1 text-xs font-medium text-background-50">
                      今日头条
                    </span>
                    <span className="rounded-full bg-background-50/90 px-2.5 py-1 text-xs font-medium text-foreground-800">
                      {catName(hero.category)}
                    </span>
                  </div>
                  <h1 className="mt-3 max-w-2xl font-heading text-2xl font-black leading-snug text-background-50 md:text-4xl">
                    {hero.title}
                  </h1>
                  <p className="mt-3 hidden max-w-xl text-sm leading-relaxed text-background-50/80 sm:block">
                    {hero.summary}
                  </p>
                  <div className="mt-4 flex items-center gap-3 text-xs text-background-50/70">
                    <span>{hero.author}</span>
                    <span className="h-1 w-1 rounded-full bg-background-50/50" />
                    <span>{hero.date}</span>
                    <span className="h-1 w-1 rounded-full bg-background-50/50" />
                    <span>{hero.readTime}阅读</span>
                  </div>
                </div>
              </Link>
            </article>

            {/* 右侧两则 */}
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-1">
              {sidePicks.map((a, i) => (
                <article key={a.id} className="group flex overflow-hidden rounded-lg border border-background-200/70 bg-background-100">
                  <Link to={`/article/${a.id}`} className="flex w-full flex-col sm:flex-row">
                    <div className="relative aspect-[16/9] w-full overflow-hidden sm:aspect-auto sm:w-40 md:w-44">
                      <img src={a.image} alt={a.title} className="h-full w-full object-cover object-top" />
                      <span className="absolute left-2 top-2 flex h-6 w-6 items-center justify-center rounded-md bg-primary-500 text-sm font-bold text-background-50">
                        {i + 1}
                      </span>
                    </div>
                    <div className="flex flex-1 flex-col justify-center p-4">
                      <span className="text-xs text-primary-700">{catName(a.category)}</span>
                      <h2 className="mt-1.5 font-heading text-base font-bold leading-snug text-foreground-950 group-hover:text-primary-700">
                        {a.title}
                      </h2>
                      <p className="mt-2 text-xs text-foreground-500">
                        {a.author} · {a.date}
                      </p>
                    </div>
                  </Link>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* ===== 滚动速览 ===== */}
        <section className="mt-8 border-y border-background-200/70 bg-background-100/60" aria-label="实时热点速览">
          <div className="mx-auto flex max-w-[1180px] items-center gap-4 px-4 py-3 md:px-6">
            <span className="flex shrink-0 items-center gap-1.5 whitespace-nowrap text-sm font-semibold text-primary-700">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary-400 opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-primary-500" />
              </span>
              今日热议
            </span>
            <div className="nice-scroll overflow-hidden">
              <div className="animate-marquee flex w-max gap-8">
                {marquee.map((t, i) => (
                  <span key={i} className="whitespace-nowrap text-sm text-foreground-600">
                    <i className="ri-flashlight-line mr-1.5 text-primary-500" />
                    {t}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ===== 栏目信息流（Tab） ===== */}
        <section className="mx-auto w-full max-w-[1180px] px-4 pt-12 md:px-6 md:pt-16" id="categories" aria-label="栏目内容">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <span className="text-xs font-semibold uppercase tracking-widest text-secondary-600">专栏精选</span>
              <h2 className="mt-2 font-heading text-2xl font-black text-foreground-950 md:text-3xl">值得花时间读的内容</h2>
            </div>
            <div className="nice-scroll flex items-center gap-1 overflow-x-auto rounded-full border border-background-200/80 bg-background-100 p-1">
              {[{ slug: "all", name: "全部" }, ...categories].map((c) => (
                <button
                  key={c.slug}
                  type="button"
                  onClick={() => setActiveTab(c.slug)}
                  className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                    activeTab === c.slug
                      ? "bg-primary-500 text-background-50"
                      : "text-foreground-600 hover:bg-background-50 hover:text-primary-700"
                  }`}
                >
                  {c.name}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-7 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {tabList.slice(0, 8).map((a) => (
              <ArticleCard key={a.id} article={a} />
            ))}
          </div>
        </section>

        {/* ===== 热榜 + 副栏 ===== */}
        <section className="mt-14 border-t border-background-200/60 bg-background-100/60" aria-label="热门榜与延伸">
          <div className="mx-auto grid max-w-[1180px] gap-10 px-4 py-12 md:px-6 md:py-16 lg:grid-cols-12">
            <div className="lg:col-span-4">
              <span className="text-xs font-semibold uppercase tracking-widest text-primary-600">Trending</span>
              <h2 className="mt-2 font-heading text-2xl font-black text-foreground-950">本周热度榜</h2>
              <p className="mt-2 text-sm text-foreground-600">编辑依据读者阅读、转发与讨论热度综合排序，每周更新。</p>
              <ol className="mt-6 space-y-3">
                {hotPicks.map((a, i) => (
                  <li key={a.id}>
                    <Link
                      to={`/article/${a.id}`}
                      className="group flex items-start gap-4 rounded-lg bg-background-50 p-3 transition-colors hover:bg-background-200/40"
                    >
                      <span
                        className={`font-heading text-3xl font-black leading-none ${
                          i < 3 ? "text-primary-500" : "text-background-400"
                        }`}
                      >
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="flex-1">
                        <span className="line-clamp-2 font-heading text-sm font-bold text-foreground-900 group-hover:text-primary-700">
                          {a.title}
                        </span>
                        <span className="mt-1 block text-xs text-foreground-500">
                          {catName(a.category)} · {a.readTime}
                        </span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ol>
            </div>

            <div className="lg:col-span-5">
              <span className="text-xs font-semibold uppercase tracking-widest text-accent-700">深度阅读</span>
              <h2 className="mt-2 font-heading text-2xl font-black text-foreground-950">延伸你的视角</h2>
              <div className="mt-6 grid gap-5 sm:grid-cols-2">
                {deepRead.slice(0, 4).map((a) => (
                  <ArticleCard key={a.id} article={a} />
                ))}
              </div>
            </div>

            <aside className="lg:col-span-3" aria-label="栏目速览">
              <h2 className="font-heading text-lg font-bold text-foreground-950">栏目速览</h2>
              <ul className="mt-5 space-y-3">
                {categories.map((c) => {
                  const Icon =
                    c.slug === "yingshi" ? "ri-film-line" : c.slug === "yinyue" ? "ri-music-2-line" : c.slug === "shenghuo" ? "ri-cup-line" : "ri-earth-line";
                  return (
                    <li key={c.slug}>
                      <Link
                        to={`/column/${c.slug}`}
                        className="group flex items-center gap-3 rounded-lg border border-background-200/70 bg-background-50 p-3.5 transition-colors hover:border-primary-200 hover:bg-primary-50"
                      >
                        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                          <i className={`${Icon} text-xl`} />
                        </span>
                        <span className="flex-1">
                          <span className="font-heading text-base font-bold text-foreground-900">{c.name}</span>
                          <span className="block text-xs text-foreground-500">{c.tagline}</span>
                        </span>
                        <i className="ri-arrow-right-line text-foreground-300 transition-transform group-hover:translate-x-1 group-hover:text-primary-600" />
                      </Link>
                    </li>
                  );
                })}
              </ul>
              <Link
                to="/about"
                className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-primary-700 hover:text-primary-600"
              >
                了解我们的内容理念 <i className="ri-arrow-right-line" />
              </Link>
            </aside>
          </div>
        </section>

        {/* ===== 数据/口碑 ===== */}
        <section className="mx-auto w-full max-w-[1180px] px-4 pt-12 md:px-6 md:pt-16" aria-label="读者口碑">
          <div className="grid gap-4 rounded-2xl border border-background-200/70 bg-background-50 p-6 sm:grid-cols-3 md:p-8">
            <div className="border-r border-background-200/70 pr-4">
              <p className="font-heading text-3xl font-black text-primary-600">4</p>
              <p className="mt-1 text-sm text-foreground-600">持续更新的内容栏目</p>
            </div>
            <div className="border-r border-background-200/70 pr-4">
              <p className="font-heading text-3xl font-black text-secondary-600">每周</p>
              <p className="mt-1 text-sm text-foreground-600">稳定上新的精选专题</p>
            </div>
            <div>
              <p className="font-heading text-3xl font-black text-accent-600">人工</p>
              <p className="mt-1 text-sm text-foreground-600">逐篇筛选与核实的编辑机制</p>
            </div>
          </div>

          <div className="mt-12 grid gap-5 md:grid-cols-3">
            {voices.map((v) => (
              <figure key={v.name} className="flex flex-col rounded-lg border border-background-200/70 bg-background-100 p-6">
                <i className="ri-double-quotes-l text-2xl text-primary-300" />
                <blockquote className="mt-3 flex-1 text-sm leading-relaxed text-foreground-700">{v.text}</blockquote>
                <figcaption className="mt-5 flex items-center gap-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-100 font-heading text-sm font-bold text-primary-700">
                    {v.name[0]}
                  </span>
                  <span>
                    <span className="block text-sm font-semibold text-foreground-900">{v.name}</span>
                    <span className="block text-xs text-foreground-500">{v.role}</span>
                  </span>
                </figcaption>
              </figure>
            ))}
          </div>
        </section>

        {/* ===== FAQ ===== */}
        <section id="faq" className="mx-auto w-full max-w-[820px] scroll-mt-24 px-4 pt-12 md:px-6 md:pt-16" aria-label="常见问题">
          <div className="text-center">
            <span className="text-xs font-semibold uppercase tracking-widest text-secondary-600">FAQ</span>
            <h2 className="mt-2 font-heading text-2xl font-black text-foreground-950 md:text-3xl">常见问题</h2>
          </div>
          <div className="mt-8 space-y-3">
            {faqs.map((f, i) => (
              <div key={f.q} className="overflow-hidden rounded-lg border border-background-200/70 bg-background-100">
                <button
                  type="button"
                  onClick={() => setFaqOpen(faqOpen === i ? -1 : i)}
                  className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
                  aria-expanded={faqOpen === i}
                >
                  <span className="font-heading text-base font-bold text-foreground-900">{f.q}</span>
                  <i className={`ri-add-line text-xl text-primary-600 transition-transform ${faqOpen === i ? "rotate-45" : ""}`} />
                </button>
                {faqOpen === i && (
                  <p className="border-t border-background-200/60 px-5 py-4 text-sm leading-relaxed text-foreground-600">
                    {f.a}
                  </p>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* ===== 订阅 ===== */}
        <section className="mx-auto w-full max-w-[1180px] px-4 pb-4 pt-12 md:px-6 md:pt-16" id="subscribe-home">
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary-600 via-primary-700 to-primary-900 px-6 py-10 md:px-10 md:py-12">
            <div className="pointer-events-none absolute -right-10 -top-10 h-52 w-52 rounded-full bg-background-50/10" />
            <div className="pointer-events-none absolute -bottom-16 right-24 h-40 w-40 rounded-full bg-background-50/10" />
            <div className="relative grid items-center gap-8 lg:grid-cols-2">
              <div className="text-background-50">
                <span className="inline-flex items-center gap-2 rounded-full bg-background-50/15 px-3 py-1 text-xs font-medium">
                  <i className="ri-mail-send-line" /> 每周内容精选
                </span>
                <h2 className="mt-4 font-heading text-2xl font-black leading-snug md:text-3xl">
                  把值得读的内容，<br className="hidden sm:block" />按时送到你面前
                </h2>
                <p className="mt-3 max-w-md text-sm leading-relaxed text-background-50/75">
                  订阅{site.name}精选，每周一次，只推真正值得花时间的内容。不打扰、不刷屏，随时可退订。
                </p>
              </div>
              <NewsletterForm id="subscribe" title="" tone="dark" subtitle="" />
            </div>
          </div>
        </section>
      </main>

      {/* 返回顶部 */}
      <button
        type="button"
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        aria-label="返回顶部"
        className={`fixed bottom-6 right-5 z-40 flex h-11 w-11 items-center justify-center rounded-full bg-primary-500 text-background-50 shadow-none transition-all hover:bg-primary-600 ${
          showTop ? "translate-y-0 opacity-100" : "pointer-events-none translate-y-4 opacity-0"
        }`}
      >
        <i className="ri-arrow-up-line text-lg" />
      </button>

      <SiteFooter />
    </div>
  );
}